import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import EmailButton from './EmailButtonExtension';
import {
    Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code,
    List, ListOrdered, Quote, Minus, Link as LinkIcon, Image as ImageIcon,
    AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Heading3,
    Undo, Redo, Unlink, Sparkles, Library, RectangleHorizontal, X
} from 'lucide-react';
import { cn } from '../lib/utils';
import MergeVariableDropdown from './MergeVariableDropdown';
import AIEmailWriter from './AIEmailWriter';
import ContentPicker from './ContentPicker';
import VoiceButton from './ui/VoiceButton';

// Toolbar Button Component
function ToolbarButton({ onClick, isActive, disabled, children, title }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={cn(
                'p-1.5 rounded transition-colors',
                isActive ? 'bg-stone-100 text-stone-900' : 'text-stone-500 hover:bg-stone-100 hover:text-stone-700',
                disabled && 'opacity-50 cursor-not-allowed'
            )}
        >
            {children}
        </button>
    );
}

// Divider Component
function ToolbarDivider() {
    return <div className="w-px h-6 bg-stone-200 mx-1" />;
}

// ─── Email Button Config Popover ────────────────────────────────────────────

const BUTTON_PRESETS = [
    { label: 'Dark', bg: '#1c1917', tc: '#ffffff' },
    { label: 'Blue', bg: '#2563eb', tc: '#ffffff' },
    { label: 'Green', bg: '#16a34a', tc: '#ffffff' },
    { label: 'Orange', bg: '#ea580c', tc: '#ffffff' },
    { label: 'Purple', bg: '#7c3aed', tc: '#ffffff' },
    { label: 'Red', bg: '#dc2626', tc: '#ffffff' },
];

function EmailButtonPopover({ editor, onClose }) {
    const isEditing = editor.isActive('emailButton');
    const currentAttrs = isEditing ? editor.getAttributes('emailButton') : {};

    const [text, setText] = useState(currentAttrs.text || 'Click Here');
    const [href, setHref] = useState(currentAttrs.href || 'https://');
    const [bg, setBg] = useState(currentAttrs.backgroundColor || '#1c1917');
    const [tc, setTc] = useState(currentAttrs.textColor || '#ffffff');
    const [radius, setRadius] = useState(currentAttrs.borderRadius || '6');
    const [alignment, setAlignment] = useState(currentAttrs.alignment || 'center');
    const [width, setWidth] = useState(currentAttrs.width || 'auto');

    const popRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (popRef.current && !popRef.current.contains(e.target)) onClose();
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [onClose]);

    const handleApply = () => {
        const attrs = { text, href, backgroundColor: bg, textColor: tc, borderRadius: radius, alignment, width };
        if (isEditing) {
            editor.commands.updateEmailButton(attrs);
        } else {
            editor.commands.insertEmailButton(attrs);
        }
        onClose();
    };

    const handleRemove = () => {
        if (isEditing) {
            editor.commands.deleteSelection();
        }
        onClose();
    };

    return (
        <div ref={popRef}
            className="absolute top-full left-0 mt-1 z-50 w-72 bg-white border border-stone-200 rounded-xl shadow-xl p-3 space-y-3"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {isEditing ? 'Edit Button' : 'Insert Button'}
                </span>
                <button onClick={onClose} className="p-0.5 text-stone-400 hover:text-stone-600">
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>

            {/* Button Text */}
            <div>
                <label className="block text-[10px] font-medium text-stone-500 mb-1">Button Text</label>
                <input value={text} onChange={e => setText(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-stone-400"
                    placeholder="Click Here" />
            </div>

            {/* URL */}
            <div>
                <label className="block text-[10px] font-medium text-stone-500 mb-1">Link URL</label>
                <input value={href} onChange={e => setHref(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-stone-400 font-mono"
                    placeholder="https://example.com" />
            </div>

            {/* Color Presets */}
            <div>
                <label className="block text-[10px] font-medium text-stone-500 mb-1">Color</label>
                <div className="flex gap-1.5 mb-2">
                    {BUTTON_PRESETS.map(p => (
                        <button key={p.label} type="button"
                            onClick={() => { setBg(p.bg); setTc(p.tc); }}
                            className={`w-7 h-7 rounded-lg border-2 transition-all ${bg === p.bg ? 'border-stone-900 scale-110' : 'border-transparent'}`}
                            style={{ backgroundColor: p.bg }}
                            title={p.label}
                        />
                    ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center gap-1.5">
                        <input type="color" value={bg} onChange={e => setBg(e.target.value)}
                            className="w-6 h-6 rounded cursor-pointer border border-stone-200" />
                        <input value={bg} onChange={e => setBg(e.target.value)}
                            className="flex-1 px-2 py-1 text-[10px] border border-stone-200 rounded font-mono" />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <input type="color" value={tc} onChange={e => setTc(e.target.value)}
                            className="w-6 h-6 rounded cursor-pointer border border-stone-200" />
                        <span className="text-[10px] text-stone-400">Text</span>
                    </div>
                </div>
            </div>

            {/* Shape & Width */}
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="block text-[10px] font-medium text-stone-500 mb-1">Shape</label>
                    <select value={radius} onChange={e => setRadius(e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded-lg">
                        <option value="0">Square</option>
                        <option value="6">Rounded</option>
                        <option value="12">More Rounded</option>
                        <option value="100">Pill</option>
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] font-medium text-stone-500 mb-1">Width</label>
                    <select value={width} onChange={e => setWidth(e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded-lg">
                        <option value="auto">Auto</option>
                        <option value="full">Full Width</option>
                    </select>
                </div>
            </div>

            {/* Alignment */}
            <div>
                <label className="block text-[10px] font-medium text-stone-500 mb-1">Alignment</label>
                <div className="flex gap-1">
                    {['left', 'center', 'right'].map(a => (
                        <button key={a} type="button"
                            onClick={() => setAlignment(a)}
                            className={`flex-1 py-1.5 text-[10px] font-medium rounded-lg capitalize ${
                                alignment === a ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-500'
                            }`}>
                            {a}
                        </button>
                    ))}
                </div>
            </div>

            {/* Live Preview */}
            <div className="bg-stone-50 rounded-lg p-3 border border-stone-100">
                <p className="text-[10px] text-stone-400 mb-2">Preview</p>
                <div style={{ textAlign: alignment }}>
                    <a href="#preview" onClick={e => e.preventDefault()}
                        style={{
                            display: width === 'full' ? 'block' : 'inline-block',
                            padding: '10px 24px',
                            backgroundColor: bg,
                            color: tc,
                            borderRadius: `${radius}px`,
                            textDecoration: 'none',
                            fontWeight: 600,
                            fontSize: '14px',
                            fontFamily: 'Arial, sans-serif',
                            textAlign: 'center',
                        }}>
                        {text || 'Click Here'}
                    </a>
                </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
                <button onClick={handleApply}
                    className="flex-1 py-1.5 text-xs font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 transition-colors">
                    {isEditing ? 'Update Button' : 'Insert Button'}
                </button>
                {isEditing && (
                    <button onClick={handleRemove}
                        className="px-3 py-1.5 text-xs font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors">
                        Remove
                    </button>
                )}
            </div>
        </div>
    );
}

// ─── Main Editor ────────────────────────────────────────────────────────────

export default function RichTextEditor({
    content = '',
    onChange,
    placeholder = 'Start writing your content...',
    editable = true,
    className = '',
    minHeight = '200px',
    showMergeVariables = false,
    showAIWriter = false,
    showContentLibrary = false,
    onImageUpload = null,
    contactId = null,
    dealId = null,
    accountId = null,
    onSubjectGenerated = null,
}) {
    const [showAIPanel, setShowAIPanel] = useState(false);
    const [showContentPicker, setShowContentPicker] = useState(false);
    const [showButtonConfig, setShowButtonConfig] = useState(false);
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                },
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-stone-700 underline hover:text-stone-800',
                },
            }),
            Image.configure({
                HTMLAttributes: {
                    class: 'max-w-full h-auto rounded-lg my-4',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Placeholder.configure({
                placeholder,
            }),
            EmailButton,
        ],
        content,
        editable,
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm max-w-none focus:outline-none',
            },
        },
    });

    // Update editor content when the content prop changes externally
    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content, false);
        }
    }, [content, editor]);

    const setLink = useCallback(() => {
        if (!editor) return;
        
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('Enter URL:', previousUrl);

        if (url === null) return;

        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }

        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    const fileInputRef = React.useRef(null);

    const addImage = useCallback(() => {
        if (!editor) return;

        if (onImageUpload) {
            // Use file picker when upload callback is provided
            fileInputRef.current?.click();
        } else {
            const url = window.prompt('Enter image URL:');
            if (url) {
                editor.chain().focus().setImage({ src: url }).run();
            }
        }
    }, [editor, onImageUpload]);

    const handleImageFileSelected = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file || !editor || !onImageUpload) return;
        e.target.value = '';
        try {
            const url = await onImageUpload(file);
            if (url) {
                editor.chain().focus().setImage({ src: url }).run();
            }
        } catch {}
    }, [editor, onImageUpload]);

    if (!editor) {
        return (
            <div className={cn('border border-stone-200 rounded-xl', className)}>
                <div className="p-4 text-center text-stone-400" style={{ minHeight }}>
                    Loading editor...
                </div>
            </div>
        );
    }

    return (
        <div className={cn('border border-stone-200 rounded-xl overflow-hidden', className)}>
            {/* Toolbar */}
            {editable && (
                <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-stone-200 bg-stone-50">
                    {/* Undo/Redo */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                        title="Undo"
                    >
                        <Undo className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                        title="Redo"
                    >
                        <Redo className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    {/* Headings */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        isActive={editor.isActive('heading', { level: 1 })}
                        title="Heading 1"
                    >
                        <Heading1 className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        isActive={editor.isActive('heading', { level: 2 })}
                        title="Heading 2"
                    >
                        <Heading2 className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        isActive={editor.isActive('heading', { level: 3 })}
                        title="Heading 3"
                    >
                        <Heading3 className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    {/* Text Formatting */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        isActive={editor.isActive('bold')}
                        title="Bold"
                    >
                        <Bold className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        isActive={editor.isActive('italic')}
                        title="Italic"
                    >
                        <Italic className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        isActive={editor.isActive('underline')}
                        title="Underline"
                    >
                        <UnderlineIcon className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        isActive={editor.isActive('strike')}
                        title="Strikethrough"
                    >
                        <Strikethrough className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleCode().run()}
                        isActive={editor.isActive('code')}
                        title="Inline Code"
                    >
                        <Code className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    {/* Alignment */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}
                        isActive={editor.isActive({ textAlign: 'left' })}
                        title="Align Left"
                    >
                        <AlignLeft className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}
                        isActive={editor.isActive({ textAlign: 'center' })}
                        title="Align Center"
                    >
                        <AlignCenter className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().setTextAlign('right').run()}
                        isActive={editor.isActive({ textAlign: 'right' })}
                        title="Align Right"
                    >
                        <AlignRight className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    {/* Lists */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        isActive={editor.isActive('bulletList')}
                        title="Bullet List"
                    >
                        <List className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        isActive={editor.isActive('orderedList')}
                        title="Numbered List"
                    >
                        <ListOrdered className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        isActive={editor.isActive('blockquote')}
                        title="Quote"
                    >
                        <Quote className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().setHorizontalRule().run()}
                        title="Horizontal Rule"
                    >
                        <Minus className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    {/* Link & Image */}
                    <ToolbarButton
                        onClick={setLink}
                        isActive={editor.isActive('link')}
                        title="Add Link"
                    >
                        <LinkIcon className="w-4 h-4" />
                    </ToolbarButton>
                    {editor.isActive('link') && (
                        <ToolbarButton
                            onClick={() => editor.chain().focus().unsetLink().run()}
                            title="Remove Link"
                        >
                            <Unlink className="w-4 h-4" />
                        </ToolbarButton>
                    )}
                    <ToolbarButton
                        onClick={addImage}
                        title="Add Image"
                    >
                        <ImageIcon className="w-4 h-4" />
                    </ToolbarButton>
                    {showContentLibrary && (
                        <ToolbarButton
                            onClick={() => setShowContentPicker(true)}
                            title="Content Library"
                        >
                            <Library className="w-4 h-4" />
                        </ToolbarButton>
                    )}
                    <VoiceButton
                        onTranscript={t => {
                            if (editor) {
                                editor.chain().focus().insertContent(t + ' ').run();
                            }
                        }}
                    />

                    <ToolbarDivider />

                    {/* Email Button */}
                    <div className="relative">
                        <ToolbarButton
                            onClick={() => setShowButtonConfig(!showButtonConfig)}
                            isActive={showButtonConfig || editor.isActive('emailButton')}
                            title="Insert Button"
                        >
                            <RectangleHorizontal className="w-4 h-4" />
                        </ToolbarButton>
                        {showButtonConfig && (
                            <EmailButtonPopover
                                editor={editor}
                                onClose={() => setShowButtonConfig(false)}
                            />
                        )}
                    </div>

                    {/* Personalization & AI Tools */}
                    {(showMergeVariables || showAIWriter) && (
                        <>
                            <ToolbarDivider />
                            {showMergeVariables && (
                                <MergeVariableDropdown
                                    onInsert={(varStr) => editor.chain().focus().insertContent(varStr).run()}
                                />
                            )}
                            {showAIWriter && (
                                <ToolbarButton
                                    onClick={() => setShowAIPanel(!showAIPanel)}
                                    isActive={showAIPanel}
                                    title="AI Email Writer"
                                >
                                    <Sparkles className="w-4 h-4" />
                                </ToolbarButton>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* AI Email Writer Panel */}
            {showAIPanel && showAIWriter && (
                <AIEmailWriter
                    contactId={contactId}
                    dealId={dealId}
                    accountId={accountId}
                    existingDraft={content}
                    onInsert={({ subject, body }) => {
                        editor.commands.setContent(body);
                        if (onSubjectGenerated && subject) onSubjectGenerated(subject);
                        setShowAIPanel(false);
                    }}
                    onClose={() => setShowAIPanel(false)}
                />
            )}

            {/* Content Library Picker */}
            {showContentLibrary && (
                <ContentPicker
                    open={showContentPicker}
                    onOpenChange={setShowContentPicker}
                    mode="insert"
                    onSelect={({ html }) => {
                        if (editor && html) {
                            editor.chain().focus().insertContent(html).run();
                        }
                    }}
                />
            )}

            {/* Editor Content */}
            <div className="p-4" style={{ minHeight }}>
                <EditorContent editor={editor} />
            </div>

            {/* Styles for the editor */}
            <style>{`
                .ProseMirror {
                    outline: none;
                }
                .ProseMirror p.is-editor-empty:first-child::before {
                    content: attr(data-placeholder);
                    float: left;
                    color: #9ca3af;
                    pointer-events: none;
                    height: 0;
                }
                .ProseMirror h1 {
                    font-size: 1.875rem;
                    font-weight: 700;
                    line-height: 1.2;
                    margin-bottom: 0.5rem;
                    color: #1e293b;
                }
                .ProseMirror h2 {
                    font-size: 1.5rem;
                    font-weight: 600;
                    line-height: 1.3;
                    margin-bottom: 0.5rem;
                    color: #1e293b;
                }
                .ProseMirror h3 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    line-height: 1.4;
                    margin-bottom: 0.5rem;
                    color: #1e293b;
                }
                .ProseMirror p {
                    margin-bottom: 0.75rem;
                    color: #475569;
                }
                .ProseMirror ul, .ProseMirror ol {
                    padding-left: 1.5rem;
                    margin-bottom: 0.75rem;
                }
                .ProseMirror ul {
                    list-style-type: disc;
                }
                .ProseMirror ol {
                    list-style-type: decimal;
                }
                .ProseMirror li {
                    margin-bottom: 0.25rem;
                }
                .ProseMirror blockquote {
                    border-left: 3px solid #6366f1;
                    padding-left: 1rem;
                    margin: 1rem 0;
                    color: #64748b;
                    font-style: italic;
                }
                .ProseMirror code {
                    background: #f1f5f9;
                    padding: 0.125rem 0.375rem;
                    border-radius: 0.25rem;
                    font-size: 0.875rem;
                    font-family: monospace;
                    color: #6366f1;
                }
                .ProseMirror pre {
                    background: #1e293b;
                    color: #e2e8f0;
                    padding: 1rem;
                    border-radius: 0.5rem;
                    overflow-x: auto;
                    margin-bottom: 0.75rem;
                }
                .ProseMirror pre code {
                    background: transparent;
                    color: inherit;
                    padding: 0;
                }
                .ProseMirror hr {
                    border: none;
                    border-top: 2px solid #e2e8f0;
                    margin: 1.5rem 0;
                }
                .ProseMirror img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 0.5rem;
                }
                .ProseMirror div[data-email-button] {
                    margin: 16px 0;
                    cursor: pointer;
                    position: relative;
                }
                .ProseMirror div[data-email-button].ProseMirror-selectednode {
                    outline: 2px solid #3b82f6;
                    outline-offset: 4px;
                    border-radius: 4px;
                }
                .ProseMirror div[data-email-button] a {
                    pointer-events: none;
                }
            `}</style>

            {/* Hidden file input for image upload */}
            {onImageUpload && (
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageFileSelected}
                />
            )}
        </div>
    );
}

// Read-only version for public display
export function RichTextViewer({ content, className = '' }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                },
            }),
            Underline,
            Link.configure({
                openOnClick: true,
                HTMLAttributes: {
                    class: 'text-stone-700 underline hover:text-stone-800',
                },
            }),
            Image.configure({
                HTMLAttributes: {
                    class: 'max-w-full h-auto rounded-lg my-4',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
        ],
        content,
        editable: false,
    });

    if (!editor) {
        return <div className="text-stone-400">Loading content...</div>;
    }

    return (
        <div className={cn('prose prose-sm max-w-none', className)}>
            <EditorContent editor={editor} />
            <style>{`
                .ProseMirror h1 { font-size: 1.875rem; font-weight: 700; color: #1e293b; margin-bottom: 0.5rem; }
                .ProseMirror h2 { font-size: 1.5rem; font-weight: 600; color: #1e293b; margin-bottom: 0.5rem; }
                .ProseMirror h3 { font-size: 1.25rem; font-weight: 600; color: #1e293b; margin-bottom: 0.5rem; }
                .ProseMirror p { margin-bottom: 0.75rem; color: #475569; }
                .ProseMirror ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 0.75rem; }
                .ProseMirror ol { list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 0.75rem; }
                .ProseMirror blockquote { border-left: 3px solid #6366f1; padding-left: 1rem; color: #64748b; font-style: italic; }
                .ProseMirror code { background: #f1f5f9; padding: 0.125rem 0.375rem; border-radius: 0.25rem; font-family: monospace; color: #6366f1; }
            `}</style>
        </div>
    );
}
